import { taskRefs } from "@krut/shared";
import type { Picked } from "../components/WorkspacePicker.tsx";
import { workspaceRel, WORKSPACE } from "./files.ts";

/* ── Types ─────────────────────────────────────────────────────────────── */

export interface Attachment {
  path: string;
  name: string;
}
export interface Tool {
  name: string;
  preview?: string;
  status: "running" | "done" | "failed";
  startedAt?: number;
}
export interface Turn {
  key: string;
  role: "user" | "assistant";
  text: string;
  attachments?: Attachment[];
  context?: Picked[];
  tools?: Tool[];
  /** Finished tasks the assistant recorded in this reply (their numbers): the reply asks whether it saved time. */
  tasks?: number[];
  streaming?: boolean;
  error?: string;
}

export const ATTACH_MARK = "\n\n[Attached files — saved in your workspace]\n";
export const FILES_ONLY_NOTE = "(Sent without a message — just the attached files.)";
export const CONTEXT_MARK = "\n\n[Use these from my workspace as context — read them before answering]\n";

/** Workspace paths the user pointed at, as the server wrote them into the message. */
function parseContext(block: string): Picked[] {
  return block
    .split("\n")
    .map((l) => /^- (\S+?)(\/)?(?: \(folder.*\))?$/.exec(l.trim()))
    .filter((m): m is RegExpExecArray => !!m && m[1]!.startsWith(WORKSPACE + "/"))
    .map((m) => ({ path: m[1]!.slice(WORKSPACE.length + 1), kind: m[2] ? ("dir" as const) : ("file" as const) }));
}

/**
 * The stored transcript → display turns, drawn the way the live stream draws them: everything the assistant
 * did between two of your messages is one reply — all its tool steps in one trail, then its final text.
 */
export function toTurns(messages: { id: string; role: string; content: string | null; toolCalls: { name: string; preview: string }[] }[]): Turn[] {
  const out: Turn[] = [];
  let reply: Turn | null = null;
  for (const m of messages) {
    if (m.role === "user") {
      const full = m.content ?? "";
      const ci = full.indexOf(CONTEXT_MARK);
      const ai = full.indexOf(ATTACH_MARK);
      const context = ci >= 0 ? parseContext(full.slice(ci + CONTEXT_MARK.length, ai > ci ? ai : undefined)) : undefined;
      const raw = ci >= 0 ? full.slice(0, ci) + (ai > ci ? full.slice(ai) : "") : full;
      const i = raw.indexOf(ATTACH_MARK);
      const attachments =
        i >= 0
          ? raw
              .slice(i + ATTACH_MARK.length)
              .split("\n")
              .map((l) => workspaceRel(l.replace(/^- /, "")))
              .filter((p): p is string => !!p)
              .map((path) => ({ path, name: path.split("/").pop() ?? path }))
          : undefined;
      const text = i >= 0 ? raw.slice(0, i) : raw;
      // Chat apps mark file-only messages for the assistant; the files already say it here.
      out.push({ key: m.id, role: "user", text: text === FILES_ONLY_NOTE ? "" : text, attachments, context });
      reply = null;
    } else if (m.role === "assistant") {
      if (!m.toolCalls.length && !m.content?.trim()) continue;
      if (!reply) out.push((reply = { key: m.id, role: "assistant", text: "", tools: [] }));
      reply.tools!.push(...m.toolCalls.map((t) => ({ name: t.name, preview: t.preview || undefined, status: "done" as const })));
      // Notes between steps ("Let me check…") give way to the final answer, as they do while streaming.
      if (m.content?.trim()) reply.text = m.content;
    } else if (m.role === "tool" && reply && m.content) {
      const ids = taskRefs(m.content);
      if (ids.length) reply.tasks = [...(reply.tasks ?? []), ...ids];
    }
  }
  return out;
}

